import React, { FC, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";

import { Loading, Typography } from "ui-library/atoms";
import { ContentBox, GridItem, GridLayout } from "ui-library/layouts";

import i18n from "core/i18n";
import { InfoMessage } from "containers/message";
import { lettersNumbersBasicInterpunctionCharacters, max50Characters } from "core/form";
import { Field, FormWideDisabledProvider } from "core/hookForm";
import { useGetFieldDirection } from "core/hooks";
import { Option } from "core/hookForm/components/Field";
import { SignatureType, SubjectTypeEnum } from "types";

import useDigisign from "../hooks/useDigisign";
import { VehicleSubjectChangeFormValues } from "../types/VehicleSubjectChangeFormValues";

type SignatureSelectionProps = {
    isDisabled?: boolean;
};

export const SignatureTypeSelection: FC<SignatureSelectionProps> = ({ isDisabled = false }) => {
    const { t } = i18n.useTranslation();
    const { idObject } = useParams();

    const { getValues } = useFormContext<VehicleSubjectChangeFormValues>();
    const changeId = getValues("idChange");

    const { checkDigisignEligibility, isDigisignEligibilityLoading, digisignEligibilityData } = useDigisign(idObject, changeId);

    const { watch } = useFormContext<VehicleSubjectChangeFormValues>();

    const selectedSignatureType = watch("signature.type");
    const isDigiSignEligible = digisignEligibilityData?.isEligible === true;

    useEffect(() => {
        checkDigisignEligibility();
    }, []);

    const signatureTypeOptions: Option[] = [
        { code: SignatureType.PHYSICAL, value: t("change.signature.type.physical") },
        { code: SignatureType.ELECTRONIC, value: t("change.signature.type.digital"), disabled: !isDigiSignEligible },
    ];

    return (
        <>
            <ContentBox fullWidth textAlign={"center"}>
                <Typography variant="h3">{t("change.signature.type.title")}</Typography>
            </ContentBox>

            <Loading loading={isDigisignEligibilityLoading}>
                {!isDigiSignEligible && (
                    <InfoMessage
                        message={
                            digisignEligibilityData.subjectType === SubjectTypeEnum.COMPANY
                                ? t("info.digiSign.notAvailable.company")
                                : t("info.digiSign.notAvailable.missingAttributes")
                        }
                    />
                )}

                {isDigiSignEligible && (
                    <InfoMessage message={t("info.digiSign.signInfo", { phoneNumber: digisignEligibilityData.phoneNumber })} />
                )}

                <FormWideDisabledProvider value={isDisabled}>
                    <ContentBox fullWidth>
                        <Field<VehicleSubjectChangeFormValues>
                            name="signature.type"
                            input="radioGroup"
                            direction={useGetFieldDirection()}
                            options={signatureTypeOptions}
                        />
                    </ContentBox>

                    {selectedSignatureType === SignatureType.PHYSICAL && (
                        <ContentBox fullWidth>
                            <GridLayout spacing={2}>
                                <GridItem xs={12} md={6}>
                                    <Field<VehicleSubjectChangeFormValues>
                                        name="signature.date"
                                        input="date"
                                        label={t("change.signature.date")}
                                    />
                                </GridItem>
                                <GridItem xs={12} md={6}>
                                    <Field<VehicleSubjectChangeFormValues>
                                        name="signature.place"
                                        input="textField"
                                        label={t("change.signature.place")}
                                        normalize={[lettersNumbersBasicInterpunctionCharacters, max50Characters]}
                                    />
                                </GridItem>
                            </GridLayout>
                        </ContentBox>
                    )}
                </FormWideDisabledProvider>
            </Loading>
        </>
    );
};
